/**
 * A-02 日報の確認。入力内容・自動計算・確認事項を見て、承認または差戻しを行う。
 */

import { WORK_PATTERN_LABELS } from '@meisui/core';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { endpoints } from '../../api/endpoints.js';
import { useMe } from '../../auth/context.js';
import {
  Card,
  DataList,
  ErrorMessage,
  IssueList,
  Loading,
  Notice,
  StatusPill,
} from '../../components/ui.js';
import {
  formatDateLabel,
  formatDateTime,
  formatDuration,
  formatTimeOrDash as time,
} from '../../lib/format.js';
import { useAsync } from '../../lib/useAsync.js';

export function ApprovalDetailPage(): React.JSX.Element {
  const me = useMe();
  const navigate = useNavigate();
  const { reportId = '' } = useParams<{ reportId: string }>();
  const state = useAsync(() => endpoints.approvalDetail(reportId), [reportId]);

  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);

  if (state.loading && !state.data) return <Loading />;
  if (state.error) return <ErrorMessage error={state.error} />;
  if (!state.data) return <Loading />;

  const { report, employee, computed, issues, changes, events, resubmitted, canApprove } =
    state.data;

  const decide = async (decision: 'approve' | 'reject'): Promise<void> => {
    setBusy(true);
    setActionError(null);
    try {
      await endpoints.decide(reportId, decision, comment);
      navigate('/approvals');
    } catch (cause) {
      setActionError(cause);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div>
        <h1 className="page-heading">{formatDateLabel(report.date)}の日報</h1>
        <p className="flex flex-wrap items-center gap-2 text-sm text-ink-muted">
          <span className="font-semibold text-ink">{employee.name}</span>
          <span>{employee.department}</span>
          <span>提出 {formatDateTime(report.submittedAt)}</span>
          <StatusPill status={report.status} locked={report.locked} />
          {resubmitted && <span className="pill pill--warn">再提出</span>}
        </p>
      </div>

      <Notice tone="info">
        承認ルート：{employee.name}（提出） → {me.employee.name}（承認） → 管理部（月次処理）
      </Notice>

      {changes.length > 0 && (
        <Card title="前回の提出からの変更">
          <ul className="space-y-1 text-sm">
            {changes.map((change) => (
              <li key={change.label}>
                {change.label}を <span className="line-through text-ink-muted">{change.from}</span>{' '}
                から <span className="font-semibold">{change.to}</span> へ修正
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="入力内容">
          <DataList
            columns={2}
            items={[
              { label: '勤務種別', value: report.workType },
              {
                label: 'パターン',
                value: computed?.pattern
                  ? `${computed.pattern}（${WORK_PATTERN_LABELS[computed.pattern]}）`
                  : '判定できません',
              },
              { label: '①直行', value: time(report.directStart) },
              { label: '②出社', value: time(report.officeIn) },
              { label: '③始業', value: time(report.workStart) },
              { label: '④終業', value: time(report.workEnd) },
              { label: '⑤退社', value: time(report.officeOut) },
              { label: '⑥直帰', value: time(report.directHome) },
              {
                label: '⑦休憩合計',
                value: report.breakTotalMinutes === null ? '—' : `${report.breakTotalMinutes}分`,
              },
              { label: '⑧時間単位年休', value: `${report.hourlyLeaveHours}時間` },
              { label: '現場名', value: report.siteName || '—' },
              { label: '休日出勤の取扱い', value: report.holidayWorkTreatment ?? '—' },
            ]}
          />
          <div className="mt-4 space-y-1 text-sm">
            <p className="text-[0.78rem] text-ink-muted">主な業務</p>
            <p>{report.taskNote || '—'}</p>
            {report.remarks && (
              <>
                <p className="mt-2 text-[0.78rem] text-ink-muted">備考</p>
                <p>{report.remarks}</p>
              </>
            )}
          </div>
        </Card>

        <Card title="自動計算結果" description={`計算版 ${me.engineVersion}`}>
          {computed ? (
            <DataList
              columns={2}
              items={[
                { label: '実働時間', value: formatDuration(computed.workMinutes) },
                { label: '移動時間', value: formatDuration(computed.travelMinutes) },
                { label: '稼働時間', value: formatDuration(computed.totalMinutes) },
                { label: 'みなし補填', value: formatDuration(computed.deemedFillMinutes) },
                { label: '日残業', value: formatDuration(computed.dailyOvertimeMinutes) },
                { label: '深夜時間', value: formatDuration(computed.nightMinutes) },
                { label: '拘束時間', value: formatDuration(computed.attendanceSpanMinutes) },
                { label: '食事手当', value: computed.mealAllowance ? '対象' : '—' },
              ]}
            />
          ) : (
            <p className="text-sm text-ink-muted">計算結果がありません。</p>
          )}
        </Card>
      </div>

      <IssueList issues={issues} />

      {events.length > 0 && (
        <Card title="この日報の履歴">
          <ul className="divide-y divide-line text-sm">
            {events.map((event, index) => (
              <li key={`${event.createdAt}-${index}`} className="flex gap-3 py-2">
                <span className="whitespace-nowrap text-ink-muted">
                  {formatDateTime(event.createdAt)}
                </span>
                <span className="font-semibold">{actionLabel(event.action)}</span>
                <span className="text-ink-sub">{event.comment}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <ErrorMessage error={actionError} />

      <Card title="承認判断">
        {!canApprove ? (
          <Notice tone="warn" title="自分の日報は承認できません">
            上長本人の日報は、上位の承認グループで確認します。
          </Notice>
        ) : report.status !== 'submitted' ? (
          <Notice tone="info">この日報はすでに処理済みです。</Notice>
        ) : (
          <>
            <label className="block">
              <span className="field-label">コメント（差戻しの場合は必須）</span>
              <textarea
                className="input"
                rows={2}
                value={comment}
                placeholder="例：休憩時間の実績を確認してください"
                onChange={(event) => setComment(event.target.value)}
              />
            </label>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn--primary"
                disabled={busy}
                onClick={() => void decide('approve')}
              >
                承認する
              </button>
              <button
                type="button"
                className="btn btn--danger"
                disabled={busy || comment.trim() === ''}
                onClick={() => void decide('reject')}
              >
                理由を入力して差戻す
              </button>
            </div>
          </>
        )}
      </Card>
    </>
  );
}

function actionLabel(action: string): string {
  const labels: Record<string, string> = {
    submit: '提出',
    withdraw: '提出の取消',
    approve: '承認',
    reject: '差戻し',
    admin_approve: '管理部が承認',
    admin_unapprove: '管理部が承認を取消',
  };
  return labels[action] ?? action;
}
